import { ViewMode, ViewModeCapabilityCoordinator } from '@mobile-mapping/web3d';
import { Icon } from '@tcweb/connect-ui';
import {
  createEffect,
  createMemo,
  createSignal,
  JSX,
  on,
  Show,
  untrack,
} from 'solid-js';
import {
  ActionMenuItem,
  ControlDropdown,
  ControlMenuItemType,
} from '@trimble/ui-components/solidjs';
import type { ViewToolsDropdownProps } from '../view-tools-dropdown/ViewToolsDropdown';
import { UnreachableCaseError } from 'ts-essentials';
import { t } from '@trimble/i18n';
import { PlanarCapability } from '@trimble/web3d-capabilities';
import { ViewToolsDropdown } from '../view-tools-dropdown/ViewToolsDropdown';
import { VIEW_MODE_ICONS } from './view-mode-icons';

const createIcon = (
  type: ViewMode['type'],
  withPadding: boolean
): JSX.Element => {
  const iconName = VIEW_MODE_ICONS[type];
  return (
    <Icon
      icon={iconName}
      size="icon-small"
      class={`flex justify-center items-center min-w-5 min-h-5${withPadding ? ' mr-2' : ''}`}
    />
  );
};

export const ViewModePicker = (props: {
  coordinator: ViewModeCapabilityCoordinator;
  viewToolActions?: ViewToolsDropdownProps['actions'];
}): JSX.Element => {
  const [viewModeValue, setViewModeValue] = createSignal<string>(
    untrack(() => props.coordinator.viewMode().type) ?? '3D'
  );

  const [cameraValue, setCameraValue] = createSignal<string>('');

  createEffect(
    on(
      () => props.coordinator.viewMode().type,
      (type) => {
        setViewModeValue(type ?? '3D');
      }
    )
  );

  createEffect(
    on(
      () =>
        [
          props.coordinator.currentCameraIndex(),
          props.coordinator.imageCapabilities(),
        ] as const,
      ([cameraIndex, ic]) => {
        if (cameraIndex == null || !ic) return;
        const planarCapabilities = ic.capabilities.filter(
          (cap) => cap instanceof PlanarCapability
        );
        const cameraName = planarCapabilities[cameraIndex]?.cameraName ?? '';
        setCameraValue(cameraName);
      }
    )
  );

  createEffect(
    on(viewModeValue, (value) => {
      if (value === props.coordinator.viewMode().type) return;
      const viewModeType = value as ViewMode['type'];
      switch (viewModeType) {
        case '3D':
          props.coordinator.setViewMode({ type: '3D' });
          break;
        case '2D':
          props.coordinator.setViewMode({ type: '2D' });
          break;
        case 'Image':
          props.coordinator.setViewMode({
            type: 'Image',
            cameraIndex: props.coordinator.currentCameraIndex() ?? 0,
          });
          break;
        case 'Panorama':
          props.coordinator.setViewMode({ type: 'Panorama' });
          break;
        default:
          throw new UnreachableCaseError(viewModeType);
      }
    })
  );

  createEffect(
    on(cameraValue, (value) => {
      if (!value) return;
      const ic = props.coordinator.imageCapabilities();
      if (!ic) return;
      const planarCapabilities = ic.capabilities.filter(
        (cap) => cap instanceof PlanarCapability
      );
      const cameraIndex = planarCapabilities.findIndex(
        (cap) => cap.cameraName === value
      );
      if (cameraIndex >= 0) {
        props.coordinator.setViewMode({
          type: 'Image',
          cameraIndex: cameraIndex,
        });
      }
    })
  );

  const cameraItems = createMemo((): ControlMenuItemType[] => {
    const ic = props.coordinator.imageCapabilities();
    if (!ic) return [];
    const planarCapabilities = ic.capabilities.filter(
      (cap) => cap instanceof PlanarCapability
    );
    return [
      {
        id: 'camera-select',
        type: 'radiogroup',
        value: cameraValue,
        setValue: setCameraValue,
        options: planarCapabilities.map((cap) => ({
          id: cap.cameraName,
          label: cap.cameraName,
        })),
      },
    ];
  });

  const currentViewModeIcon = createMemo((): JSX.Element => {
    const type = props.coordinator.viewMode().type ?? '3D';
    return createIcon(type, false);
  });

  const viewToolActions = createMemo<ActionMenuItem[]>(
    () => props.viewToolActions?.() ?? []
  );

  const viewModeItems = createMemo((): ControlMenuItemType[] => [
    {
      id: 'view-mode',
      type: 'radiogroup',
      value: viewModeValue,
      setValue: setViewModeValue,
      options: (['3D', '2D', 'Image', 'Panorama'] as const).map((id) => ({
        id,
        label: t(`mobileMapping.viewModes.${id}`),
        icon: createIcon(id, true),
      })),
    },
  ]);

  return (
    <div class="flex flex-row items-center">
      <div class="contents" data-testid="view-modes">
        <ControlDropdown
          anchorIcon={currentViewModeIcon()}
          items={viewModeItems()}
        />
      </div>
      <Show
        when={
          (props.coordinator.imageCapabilities()?.capabilities.length ?? 0) >
            0 && props.coordinator.viewMode().type === 'Image'
        }
      >
        <div data-testid="camera-picker">
          <ControlDropdown
            anchorIcon="tc-icon-camera-alt"
            items={cameraItems()}
          />
        </div>
      </Show>
      <ViewToolsDropdown actions={viewToolActions} />
    </div>
  );
};
